/**
 * IndexedDB Service for ADHD Planner
 * Provides robust, offline-first storage for tasks, templates, and proofs
 */

const DB_NAME = 'adhd-planner-db';
const DB_VERSION = 2; // Upgraded for new stores

// Store names
const STORES = {
  TASKS: 'tasks',
  COMPLETED: 'completed',
  TEMPLATES: 'templates',
  PROOFS: 'proofs',
  SYNC_QUEUE: 'syncQueue',
  HISTORY: 'actionHistory',
  SETTINGS: 'settings',
  // New stores for v2
  CUSTOM_TEMPLATES: 'customTemplates',    // User-created week templates
  AUTO_GENERATION: 'autoGeneration',       // Auto-generation settings
  USER_SCHEDULE: 'userSchedule',           // Personal schedule (wake, sleep, meals)
  GAMIFICATION: 'gamification',            // XP, levels, badges, streaks
  MOOD_TRACKING: 'moodTracking',           // Mood and energy tracking
  WEEKLY_STATS: 'weeklyStats',             // Weekly statistics
};

class TaskDatabase {
  constructor() {
    this.db = null;
    this.isReady = false;
    this.readyPromise = null;
  }

  // Initialize the database
  async init() {
    if (this.readyPromise) return this.readyPromise;

    this.readyPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isReady = true;
        console.log('IndexedDB ready');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        this.createStores(db);
      };
    });

    return this.readyPromise;
  }

  // Create object stores with indexes
  createStores(db) {
    // Tasks store
    if (!db.objectStoreNames.contains(STORES.TASKS)) {
      const taskStore = db.createObjectStore(STORES.TASKS, { keyPath: 'id' });
      taskStore.createIndex('date', 'date', { unique: false });
      taskStore.createIndex('category', 'category', { unique: false });
      taskStore.createIndex('createdAt', 'createdAt', { unique: false });
      taskStore.createIndex('templateId', 'templateId', { unique: false });
      taskStore.createIndex('recurrenceId', 'recurrenceId', { unique: false });
    }

    // Completed tasks tracking
    if (!db.objectStoreNames.contains(STORES.COMPLETED)) {
      const completedStore = db.createObjectStore(STORES.COMPLETED, { keyPath: 'key' });
      completedStore.createIndex('date', 'date', { unique: false });
      completedStore.createIndex('taskId', 'taskId', { unique: false });
    }

    // Templates store
    if (!db.objectStoreNames.contains(STORES.TEMPLATES)) {
      const templateStore = db.createObjectStore(STORES.TEMPLATES, { keyPath: 'id' });
      templateStore.createIndex('name', 'name', { unique: false });
      templateStore.createIndex('category', 'category', { unique: false });
    }

    // Proofs store (for task completion photos)
    if (!db.objectStoreNames.contains(STORES.PROOFS)) {
      const proofStore = db.createObjectStore(STORES.PROOFS, { keyPath: 'id' });
      proofStore.createIndex('taskId', 'taskId', { unique: false });
      proofStore.createIndex('date', 'date', { unique: false });
    }

    // Sync queue for offline operations
    if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
      const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
      syncStore.createIndex('type', 'type', { unique: false });
      syncStore.createIndex('status', 'status', { unique: false });
    }

    // Action history for undo/redo
    if (!db.objectStoreNames.contains(STORES.HISTORY)) {
      const historyStore = db.createObjectStore(STORES.HISTORY, { keyPath: 'id', autoIncrement: true });
      historyStore.createIndex('timestamp', 'timestamp', { unique: false });
    }

    // Settings store
    if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
      db.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
    }

    // === NEW STORES FOR V2 ===

    // Custom Templates store (user-created week templates)
    if (!db.objectStoreNames.contains(STORES.CUSTOM_TEMPLATES)) {
      const customTemplateStore = db.createObjectStore(STORES.CUSTOM_TEMPLATES, { keyPath: 'id' });
      customTemplateStore.createIndex('name', 'name', { unique: false });
      customTemplateStore.createIndex('createdAt', 'createdAt', { unique: false });
      customTemplateStore.createIndex('isDefault', 'isDefault', { unique: false });
    }

    // Auto Generation settings store
    if (!db.objectStoreNames.contains(STORES.AUTO_GENERATION)) {
      db.createObjectStore(STORES.AUTO_GENERATION, { keyPath: 'id' });
    }

    // User Schedule store (personal timings)
    if (!db.objectStoreNames.contains(STORES.USER_SCHEDULE)) {
      db.createObjectStore(STORES.USER_SCHEDULE, { keyPath: 'id' });
    }

    // Gamification store (XP, levels, badges)
    if (!db.objectStoreNames.contains(STORES.GAMIFICATION)) {
      const gamificationStore = db.createObjectStore(STORES.GAMIFICATION, { keyPath: 'id' });
      gamificationStore.createIndex('date', 'date', { unique: false });
      gamificationStore.createIndex('type', 'type', { unique: false });
    }

    // Mood Tracking store
    if (!db.objectStoreNames.contains(STORES.MOOD_TRACKING)) {
      const moodStore = db.createObjectStore(STORES.MOOD_TRACKING, { keyPath: 'id' });
      moodStore.createIndex('date', 'date', { unique: false });
      moodStore.createIndex('timestamp', 'timestamp', { unique: false });
    }

    // Weekly Stats store
    if (!db.objectStoreNames.contains(STORES.WEEKLY_STATS)) {
      const weeklyStore = db.createObjectStore(STORES.WEEKLY_STATS, { keyPath: 'weekKey' });
      weeklyStore.createIndex('year', 'year', { unique: false });
    }
  }

  // Ensure database is ready
  async ensureReady() {
    if (!this.isReady) {
      await this.init();
    }
    return this.db;
  }

  // Generic CRUD operations
  async add(storeName, data) {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.add(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async put(storeName, data) {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.put(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async get(storeName, key) {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName, key) {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAll(storeName) {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async getByIndex(storeName, indexName, value) {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const index = store.index(indexName);
      const request = index.getAll(value);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async clear(storeName) {
    const db = await this.ensureReady();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Task-specific methods
  async addTask(task) {
    const taskWithMeta = {
      ...task,
      id: task.id || `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: task.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await this.put(STORES.TASKS, taskWithMeta);
    return taskWithMeta;
  }

  // Alias for saveTask (used by cloud sync)
  async saveTask(task) {
    return this.addTask(task);
  }

  // Save a completed task
  async saveCompletedTask(task) {
    const completedTask = {
      ...task,
      key: task.key || task.id,
      completed: true,
      completedAt: task.completedAt || new Date().toISOString(),
    };
    await this.put(STORES.COMPLETED, completedTask);
    return completedTask;
  }

  // Get all completed tasks
  async getAllCompletedTasks() {
    return this.getAll(STORES.COMPLETED);
  }

  // Get all tasks
  async getAllTasks() {
    return this.getAll(STORES.TASKS);
  }

  async updateTask(taskId, updates) {
    const existing = await this.get(STORES.TASKS, taskId);
    if (!existing) throw new Error('Task not found');

    const updated = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    await this.put(STORES.TASKS, updated);
    return updated;
  }

  async deleteTask(taskId) {
    const task = await this.get(STORES.TASKS, taskId);
    await this.delete(STORES.TASKS, taskId);
    return task; // Return deleted task for undo
  }

  async getTasksByDate(dateStr) {
    return this.getByIndex(STORES.TASKS, 'date', dateStr);
  }

  async searchTasks(query) {
    const allTasks = await this.getAll(STORES.TASKS);
    const lowerQuery = query.toLowerCase();
    return allTasks.filter(task =>
      task.task?.toLowerCase().includes(lowerQuery) ||
      task.desc?.toLowerCase().includes(lowerQuery) ||
      task.instructions?.some(i => i.toLowerCase().includes(lowerQuery))
    );
  }

  // Completed tasks methods
  async setCompleted(key, value, metadata = {}) {
    await this.put(STORES.COMPLETED, {
      key,
      value,
      date: metadata.date || new Date().toISOString().split('T')[0],
      taskId: metadata.taskId,
      completedAt: value ? new Date().toISOString() : null,
    });
  }

  async isCompleted(key) {
    const record = await this.get(STORES.COMPLETED, key);
    return record?.value || false;
  }

  async getCompletedForDate(dateStr) {
    return this.getByIndex(STORES.COMPLETED, 'date', dateStr);
  }

  // Template methods
  async addTemplate(template) {
    const templateWithMeta = {
      ...template,
      id: template.id || `template-${Date.now()}`,
      createdAt: new Date().toISOString(),
    };
    await this.put(STORES.TEMPLATES, templateWithMeta);
    return templateWithMeta;
  }

  async getAllTemplates() {
    return this.getAll(STORES.TEMPLATES);
  }

  async deleteTemplate(templateId) {
    return this.delete(STORES.TEMPLATES, templateId);
  }

  async applyTemplate(templateId, date) {
    const template = await this.get(STORES.TEMPLATES, templateId);
    if (!template) throw new Error('Template not found');

    const newTask = await this.addTask({
      ...template.task,
      id: undefined, // Generate new ID
      date,
      templateId,
      createdAt: undefined,
    });
    return newTask;
  }

  // Proof methods
  async addProof(proof) {
    const proofWithMeta = {
      ...proof,
      id: proof.id || `proof-${Date.now()}`,
      createdAt: new Date().toISOString(),
    };
    await this.put(STORES.PROOFS, proofWithMeta);
    return proofWithMeta;
  }

  // Alias for saveProof (used by cloud sync)
  async saveProof(proof) {
    return this.addProof(proof);
  }

  async getProofsForTask(taskId) {
    return this.getByIndex(STORES.PROOFS, 'taskId', taskId);
  }

  async getAllProofs() {
    return this.getAll(STORES.PROOFS);
  }

  async deleteProof(proofId) {
    return this.delete(STORES.PROOFS, proofId);
  }

  // Sync queue methods (for offline-first)
  async addToSyncQueue(operation) {
    const queueItem = {
      ...operation,
      status: 'pending',
      createdAt: new Date().toISOString(),
      retries: 0,
    };
    return this.add(STORES.SYNC_QUEUE, queueItem);
  }

  async getPendingSyncs() {
    return this.getByIndex(STORES.SYNC_QUEUE, 'status', 'pending');
  }

  async markSynced(id) {
    const item = await this.get(STORES.SYNC_QUEUE, id);
    if (item) {
      item.status = 'synced';
      item.syncedAt = new Date().toISOString();
      await this.put(STORES.SYNC_QUEUE, item);
    }
  }

  async markSyncFailed(id, error) {
    const item = await this.get(STORES.SYNC_QUEUE, id);
    if (item) {
      item.retries = (item.retries || 0) + 1;
      item.lastError = error;
      item.status = item.retries >= 3 ? 'failed' : 'pending';
      await this.put(STORES.SYNC_QUEUE, item);
    }
  }

  // Action history for undo/redo
  async pushAction(action) {
    const historyItem = {
      ...action,
      timestamp: new Date().toISOString(),
    };
    return this.add(STORES.HISTORY, historyItem);
  }

  async getRecentHistory(limit = 20) {
    const all = await this.getAll(STORES.HISTORY);
    return all
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, limit);
  }

  async clearOldHistory(keepCount = 50) {
    const all = await this.getAll(STORES.HISTORY);
    if (all.length > keepCount) {
      const toDelete = all
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(keepCount);

      for (const item of toDelete) {
        await this.delete(STORES.HISTORY, item.id);
      }
    }
  }

  // Migration from localStorage
  async migrateFromLocalStorage() {
    try {
      // Migrate custom tasks
      const customTasks = localStorage.getItem('adhd-planner-custom-tasks');
      if (customTasks) {
        const tasks = JSON.parse(customTasks);
        for (const task of tasks) {
          await this.addTask(task);
        }
        console.log(`Migrated ${tasks.length} tasks from localStorage`);
      }

      // Migrate completed tasks
      const completed = localStorage.getItem('adhd-planner-completed');
      if (completed) {
        const completedData = JSON.parse(completed);
        for (const [key, value] of Object.entries(completedData)) {
          await this.setCompleted(key, value);
        }
        console.log('Migrated completed tasks from localStorage');
      }

      // Mark migration as done
      localStorage.setItem('adhd-planner-migrated-to-idb', 'true');

      return true;
    } catch (error) {
      console.error('Migration error:', error);
      return false;
    }
  }

  async needsMigration() {
    const migrated = localStorage.getItem('adhd-planner-migrated-to-idb');
    const hasOldData = localStorage.getItem('adhd-planner-custom-tasks');
    return !migrated && hasOldData;
  }

  // Export all data
  async exportAll() {
    return {
      tasks: await this.getAll(STORES.TASKS),
      completed: await this.getAll(STORES.COMPLETED),
      templates: await this.getAll(STORES.TEMPLATES),
      proofs: await this.getAll(STORES.PROOFS),
      exportedAt: new Date().toISOString(),
      version: DB_VERSION,
    };
  }

  // Import data
  async importAll(data) {
    if (data.tasks && Array.isArray(data.tasks)) {
      for (const task of data.tasks) {
        await this.put(STORES.TASKS, task);
      }
    }
    if (data.completed && Array.isArray(data.completed)) {
      for (const item of data.completed) {
        await this.put(STORES.COMPLETED, item);
      }
    }
    if (data.templates && Array.isArray(data.templates)) {
      for (const template of data.templates) {
        await this.put(STORES.TEMPLATES, template);
      }
    }
    if (data.proofs && Array.isArray(data.proofs)) {
      for (const proof of data.proofs) {
        await this.put(STORES.PROOFS, proof);
      }
    }
    // Import new stores
    if (data.customTemplates && Array.isArray(data.customTemplates)) {
      for (const template of data.customTemplates) {
        await this.put(STORES.CUSTOM_TEMPLATES, template);
      }
    }
    // Handle gamification - can be array or object
    if (data.gamification) {
      if (Array.isArray(data.gamification)) {
        for (const item of data.gamification) {
          await this.put(STORES.GAMIFICATION, item);
        }
      } else {
        // It's a stats object
        await this.saveGamificationStats(data.gamification);
      }
    }
    if (data.moodTracking && Array.isArray(data.moodTracking)) {
      for (const item of data.moodTracking) {
        await this.put(STORES.MOOD_TRACKING, item);
      }
    }
    // Handle settings object
    if (data.settings && typeof data.settings === 'object') {
      if (data.settings.schedule) {
        await this.saveUserSchedule(data.settings.schedule);
      }
      if (data.settings.autoGeneration) {
        await this.saveAutoGenerationSettings(data.settings.autoGeneration);
      }
    }
    // Handle autoGeneration directly
    if (data.autoGeneration && typeof data.autoGeneration === 'object' && !Array.isArray(data.autoGeneration)) {
      await this.saveAutoGenerationSettings(data.autoGeneration);
    }
    // Handle userSchedule directly
    if (data.userSchedule && typeof data.userSchedule === 'object' && !Array.isArray(data.userSchedule)) {
      await this.saveUserSchedule(data.userSchedule);
    }
  }

  // ============================================
  // CUSTOM TEMPLATES METHODS (Week templates)
  // ============================================

  async saveCustomTemplate(template) {
    const templateWithMeta = {
      ...template,
      id: template.id || `custom_template_${Date.now()}`,
      createdAt: template.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await this.put(STORES.CUSTOM_TEMPLATES, templateWithMeta);
    return templateWithMeta;
  }

  async getAllCustomTemplates() {
    return this.getAll(STORES.CUSTOM_TEMPLATES);
  }

  async getCustomTemplate(templateId) {
    return this.get(STORES.CUSTOM_TEMPLATES, templateId);
  }

  async deleteCustomTemplate(templateId) {
    return this.delete(STORES.CUSTOM_TEMPLATES, templateId);
  }

  async updateCustomTemplate(templateId, updates) {
    const existing = await this.get(STORES.CUSTOM_TEMPLATES, templateId);
    if (!existing) throw new Error('Custom template not found');

    const updated = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    await this.put(STORES.CUSTOM_TEMPLATES, updated);
    return updated;
  }

  // ============================================
  // AUTO GENERATION SETTINGS METHODS
  // ============================================

  async getAutoGenerationSettings() {
    const settings = await this.get(STORES.AUTO_GENERATION, 'settings');
    return settings || {
      id: 'settings',
      enabled: false,
      mode: 'ask',
      dayOfWeek: 1,
      template: 'balanced',
      templateId: null,
      includeWeekends: true,
      autoAddHygiene: true,
      autoAddBreaks: true,
      lastGeneratedWeek: null,
    };
  }

  async saveAutoGenerationSettings(settings) {
    const settingsWithMeta = {
      ...settings,
      id: 'settings',
      updatedAt: new Date().toISOString(),
    };
    await this.put(STORES.AUTO_GENERATION, settingsWithMeta);
    return settingsWithMeta;
  }

  async updateLastGeneratedWeek(weekKey) {
    const settings = await this.getAutoGenerationSettings();
    settings.lastGeneratedWeek = weekKey;
    await this.saveAutoGenerationSettings(settings);
  }

  // ============================================
  // USER SCHEDULE METHODS
  // ============================================

  async getUserSchedule() {
    const schedule = await this.get(STORES.USER_SCHEDULE, 'schedule');
    return schedule || {
      id: 'schedule',
      wakeTime: '07:00',
      sleepTime: '23:00',
      breakfastTime: '08:00',
      lunchTime: '12:30',
      dinnerTime: '19:30',
      workStartTime: '09:00',
      workEndTime: '18:00',
    };
  }

  async saveUserSchedule(schedule) {
    const scheduleWithMeta = {
      ...schedule,
      id: 'schedule',
      updatedAt: new Date().toISOString(),
    };
    await this.put(STORES.USER_SCHEDULE, scheduleWithMeta);
    return scheduleWithMeta;
  }

  // ============================================
  // GAMIFICATION METHODS
  // ============================================

  async getGamificationStats() {
    const stats = await this.get(STORES.GAMIFICATION, 'stats');
    return stats || {
      id: 'stats',
      totalXP: 0,
      level: 1,
      currentStreak: 0,
      longestStreak: 0,
      tasksCompleted: 0,
      vitalTasksCompleted: 0,
      focusSessionsCompleted: 0,
      proofsAdded: 0,
      badgesUnlocked: [],
      lastActivityDate: null,
    };
  }

  async saveGamificationStats(stats) {
    const statsWithMeta = {
      ...stats,
      id: 'stats',
      updatedAt: new Date().toISOString(),
    };
    await this.put(STORES.GAMIFICATION, statsWithMeta);
    return statsWithMeta;
  }

  async addXPEvent(xpData) {
    const event = {
      id: `xp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'xp',
      amount: xpData.amount,
      reason: xpData.reason,
      taskId: xpData.taskId,
      date: new Date().toISOString().split('T')[0],
      timestamp: new Date().toISOString(),
    };
    await this.add(STORES.GAMIFICATION, event);
    return event;
  }

  async getXPEventsForDate(dateStr) {
    const events = await this.getByIndex(STORES.GAMIFICATION, 'date', dateStr);
    return events.filter(e => e.type === 'xp');
  }

  // ============================================
  // MOOD TRACKING METHODS
  // ============================================

  async addMoodEntry(entry) {
    const moodEntry = {
      id: `mood_${Date.now()}`,
      mood: entry.mood,
      energy: entry.energy,
      notes: entry.notes || '',
      date: entry.date || new Date().toISOString().split('T')[0],
      timestamp: new Date().toISOString(),
    };
    await this.add(STORES.MOOD_TRACKING, moodEntry);
    return moodEntry;
  }

  async getMoodEntriesForDate(dateStr) {
    return this.getByIndex(STORES.MOOD_TRACKING, 'date', dateStr);
  }

  async getLatestMoodEntry() {
    const today = new Date().toISOString().split('T')[0];
    const entries = await this.getMoodEntriesForDate(today);
    if (entries.length === 0) return null;
    return entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
  }

  async getMoodHistory(days = 30) {
    const allMoods = await this.getAll(STORES.MOOD_TRACKING);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    return allMoods
      .filter(m => new Date(m.timestamp) >= cutoffDate)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  // ============================================
  // WEEKLY STATS METHODS
  // ============================================

  async getWeeklyStats(weekKey) {
    const stats = await this.get(STORES.WEEKLY_STATS, weekKey);
    return stats || {
      weekKey,
      year: parseInt(weekKey.split('-W')[0]),
      weekNumber: parseInt(weekKey.split('-W')[1]),
      tasksCompleted: 0,
      tasksCreated: 0,
      vitalTasksCompleted: 0,
      totalXP: 0,
      focusMinutes: 0,
      averageMood: null,
      averageEnergy: null,
      streakDays: 0,
      createdAt: new Date().toISOString(),
    };
  }

  async saveWeeklyStats(stats) {
    const statsWithMeta = {
      ...stats,
      updatedAt: new Date().toISOString(),
    };
    await this.put(STORES.WEEKLY_STATS, statsWithMeta);
    return statsWithMeta;
  }

  async updateWeeklyStats(weekKey, updates) {
    const existing = await this.getWeeklyStats(weekKey);
    const updated = { ...existing, ...updates };
    return this.saveWeeklyStats(updated);
  }

  // Get current week key (ISO format: YYYY-Www)
  getCurrentWeekKey() {
    const now = new Date();
    const year = now.getFullYear();
    const firstDayOfYear = new Date(year, 0, 1);
    const pastDaysOfYear = (now - firstDayOfYear) / 86400000;
    const weekNumber = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    return `${year}-W${weekNumber.toString().padStart(2, '0')}`;
  }

  // ============================================
  // EXTENDED EXPORT/IMPORT
  // ============================================

  // Extended export with all new stores
  async exportAllExtended() {
    return {
      tasks: await this.getAll(STORES.TASKS),
      completed: await this.getAll(STORES.COMPLETED),
      templates: await this.getAll(STORES.TEMPLATES),
      proofs: await this.getAll(STORES.PROOFS),
      customTemplates: await this.getAll(STORES.CUSTOM_TEMPLATES),
      autoGeneration: await this.getAutoGenerationSettings(),
      userSchedule: await this.getUserSchedule(),
      gamification: await this.getGamificationStats(),
      moodTracking: await this.getAll(STORES.MOOD_TRACKING),
      weeklyStats: await this.getAll(STORES.WEEKLY_STATS),
      exportedAt: new Date().toISOString(),
      version: DB_VERSION,
    };
  }

  // Migrate localStorage data to IndexedDB for new stores
  async migrateNewStoresFromLocalStorage() {
    try {
      // Migrate custom templates
      const customTemplates = localStorage.getItem('adhd_custom_templates');
      if (customTemplates) {
        const templates = JSON.parse(customTemplates);
        for (const template of templates) {
          await this.saveCustomTemplate(template);
        }
        console.log(`Migrated ${templates.length} custom templates`);
      }

      // Migrate auto generation settings
      const autoGenSettings = localStorage.getItem('adhd_auto_generation');
      if (autoGenSettings) {
        const settings = JSON.parse(autoGenSettings);
        await this.saveAutoGenerationSettings(settings);
        console.log('Migrated auto generation settings');
      }

      // Migrate user schedule
      const scheduleSettings = localStorage.getItem('adhd_schedule_settings');
      if (scheduleSettings) {
        const schedule = JSON.parse(scheduleSettings);
        await this.saveUserSchedule(schedule);
        console.log('Migrated user schedule');
      }

      // Mark migration as done
      localStorage.setItem('adhd-planner-migrated-v2', 'true');
      return true;
    } catch (error) {
      console.error('Migration error (v2):', error);
      return false;
    }
  }

  async needsMigrationV2() {
    const migrated = localStorage.getItem('adhd-planner-migrated-v2');
    return !migrated;
  }
}

// Singleton instance
const taskDatabase = new TaskDatabase();
export default taskDatabase;
export { STORES };
